import { collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { AdsAccount, AdsAccountProvider } from '@superfunnel/types/ads-account';
import { AdsAccountKeyword } from '@superfunnel/types/ads-account-keyword';
import { AdsCampaign } from '@superfunnel/types/ads-campaign';
import { AdsGroup } from '@superfunnel/types/ads-group';
import {
  AdsGroupAd,
  FacebookAdMediaRefreshResult,
  TikTokAdMediaRefreshResult,
} from '@superfunnel/types/ads-group-ad';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

export const adsAccountsColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'adsAccounts').withConverter(
    emptyConverter<AdsAccount>(),
  );
};

export const activeAdsAccountsQuery = (accountId: string) => {
  return query(adsAccountsColRef(accountId), where('status', '==', 'active'));
};

export const approveConnectRequest = async (
  accountId: string,
  requestId: string,
  adsAccountsIds: string[],
  connectBoth?: boolean,
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      requestId: string;
      adsAccountsIds: string[];
      connectBoth: boolean;
    },
    { ok: true; connectorError?: string }
  >(functions, 'accounts/adsAccounts/approveConnectRequest');
  const res = await fn({
    accountId,
    requestId,
    adsAccountsIds,
    connectBoth: connectBoth ?? false,
  });

  return res.data;
};

export const resyncAdsAccount = async (
  accountId: string,
  adsAccountsIds: string[],
) => {
  const fn = httpsCallable(functions, 'accounts/adsAccounts/resync');
  const res = await fn({ accountId, adsAccountsIds });

  return res.data;
};

export const disconnectAdsAccount = async (
  accountId: string,
  adsAccountId: string,
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      adsAccountId: string;
    },
    { ok: true }
  >(functions, 'accounts/adsAccounts/disconnect');
  const res = await fn({
    accountId,
    adsAccountId,
  });

  return res.data;
};

export interface AdsEntityMetrics {
  clicks?: number;
  impressions?: number;
  ctr?: number;
  avgCpc?: number;
  cost?: number;
  conversions?: number;
  conversionsValue?: number;
  costPerConversion?: number;
  reach?: number;
  frequency?: number;
}

export type AdsMetricsLevel = 'campaign' | 'adGroup' | 'ad';

/**
 * Server-side ordering for the cached structure. `field` is validated against
 * a per-level allowlist in the backend, because it becomes a Mongo sort key.
 */
export interface AdsSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface AdsMetricsEntity {
  provider: AdsAccountProvider;
  adsAccountId: string;
  id: string;
}

/** Ids repeat across providers and ad accounts, so every key carries all three. */
export const adsMetricsKey = (entity: AdsMetricsEntity) =>
  `${entity.provider}:${entity.adsAccountId}:${entity.id}`;

/**
 * Live, range-scoped metrics for an explicit set of entities — the rows the
 * table is currently showing. Structure comes from the cache; numbers come
 * from the ad accounts.
 */
export const getAdsMetrics = async (
  accountId: string,
  fromDate: string,
  toDate: string,
  level: AdsMetricsLevel,
  entities: AdsMetricsEntity[],
): Promise<Record<string, AdsEntityMetrics>> => {
  const fn = httpsCallable<
    {
      accountId: string;
      fromDate: string;
      toDate: string;
      level: AdsMetricsLevel;
      entities: AdsMetricsEntity[];
    },
    Record<string, AdsEntityMetrics>
  >(functions, 'accounts/adsAccounts/getMetrics');
  const res = await fn({ accountId, fromDate, toDate, level, entities });

  return res.data;
};

export interface AdsMetricsTotalsFilter {
  campaignId?: string | string[];
  adGroupId?: string | string[];
  adsAccountId?: string;
  provider: AdsAccountProvider;
}

/**
 * Range totals for every row matching the filters, not only the loaded pages.
 * The same call serves the compared period — pass the earlier dates.
 */
export const getAdsMetricsTotals = async (
  accountId: string,
  fromDate: string,
  toDate: string,
  level: AdsMetricsLevel,
  filters: AdsMetricsTotalsFilter[],
  options?: { status?: string; search?: string },
): Promise<AdsEntityMetrics> => {
  const fn = httpsCallable<
    {
      accountId: string;
      fromDate: string;
      toDate: string;
      level: AdsMetricsLevel;
      filters: AdsMetricsTotalsFilter[];
      status?: string;
      search?: string;
    },
    AdsEntityMetrics
  >(functions, 'accounts/adsAccounts/getMetricsTotals');
  const res = await fn({
    accountId,
    fromDate,
    toDate,
    level,
    filters,
    ...options,
  });

  return res.data;
};

export const getCachedCampaigns = async (
  accountId: string,
  providers: string[] = ['google'],
  filters?: {
    campaignId?: string | string[];
    adsAccountId: string;
    provider: AdsAccountProvider;
  }[],
  limit: number = 1000,
  offset: number = 0,
  options?: {
    status?: string;
    search?: string;
    sort?: AdsSort;
    withDestinations?: boolean;
  },
): Promise<AdsCampaign[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      providers: string[];
      filters?: {
        campaignId?: string | string[];
        adsAccountId: string;
        provider: AdsAccountProvider;
      }[];
      limit: number;
      offset: number;
      status?: string;
      search?: string;
      sort?: AdsSort;
      withDestinations?: boolean;
    },
    AdsCampaign[]
  >(functions, 'accounts/adsAccounts/getCachedCampaigns');
  const res = await fn({
    accountId,
    providers,
    ...(filters ? { filters } : {}),
    limit,
    offset,
    ...options,
  });

  return res.data;
};

export const getCachedAccountAdGroups = async (
  accountId: string,
  filters: {
    campaignId?: string | string[];
    adsAccountId: string;
    provider: AdsAccountProvider;
  }[],
  limit: number = 1000,
  offset: number = 0,
  options?: {
    status?: string;
    search?: string;
    sort?: AdsSort;
    withDestinations?: boolean;
  },
): Promise<AdsGroup[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      filters: {
        campaignId?: string | string[];
        adsAccountId: string;
        provider: AdsAccountProvider;
      }[];
      limit: number;
      offset: number;
      status?: string;
      search?: string;
      sort?: AdsSort;
      withDestinations?: boolean;
    },
    AdsGroup[]
  >(functions, 'accounts/adsAccounts/getCachedAdGroups');
  const res = await fn({
    accountId,
    filters,
    limit,
    offset,
    ...options,
  });

  return res.data;
};

interface GetCachedAdGroupAdsRequest {
  accountId: string;
  filters: {
    campaignId?: string | string[];
    adGroupId?: string | string[];
    adsAccountId: string;
    provider: AdsAccountProvider;
    creativeTypes?: string[];
  }[];
  limit: number;
  offset: number;
  status?: string;
  creativeType?: string[];
  campaignIds?: string[];
  ids?: string[];
  search?: string;
  sort?: AdsSort;
  withDestinations?: boolean;
}

const callGetCachedAdGroupAds = async (
  request: GetCachedAdGroupAdsRequest,
): Promise<AdsGroupAd[]> => {
  const fn = httpsCallable<GetCachedAdGroupAdsRequest, AdsGroupAd[]>(
    functions,
    'accounts/adsAccounts/getCachedAdGroupAds',
  );
  const res = await fn(request);

  return res.data;
};

export const getCachedAccountAds = async (
  accountId: string,
  filters: GetCachedAdGroupAdsRequest['filters'],
  limit: number = 1000,
  offset: number = 0,
  options?: {
    status?: string;
    creativeType?: string[];
    campaignIds?: string[];
    ids?: string[];
    search?: string;
    sort?: AdsSort;
    withDestinations?: boolean;
  },
): Promise<AdsGroupAd[]> => {
  return callGetCachedAdGroupAds({
    accountId,
    filters,
    limit,
    offset,
    ...options,
  });
};

export const getAccountKeywords = async (
  accountId: string,
  search: string,
  filter: unknown,
  limit: number,
  skip: number,
): Promise<AdsAccountKeyword[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      provider: 'google';
      query: string;
      filter: unknown;
      limit: number;
      skip: number;
    },
    (AdsAccountKeyword & { _id?: string })[]
  >(functions, 'accounts/adsAccounts/getAdsAccountsKeywords');
  const res = await fn({
    accountId,
    provider: 'google',
    query: search,
    filter,
    limit,
    skip,
  });

  return res.data.map((keyword) => ({
    ...keyword,
    id: keyword._id ?? keyword.id,
  }));
};

export const refreshFacebookAdMedia = async (
  accountId: string,
  adsAccountId: string,
  adId: string,
  mediaType: 'image' | 'video',
): Promise<FacebookAdMediaRefreshResult> => {
  const fn = httpsCallable<
    {
      accountId: string;
      adsAccountId: string;
      adId: string;
      mediaType: 'image' | 'video';
    },
    FacebookAdMediaRefreshResult
  >(functions, 'accounts/adsAccounts/refreshFacebookAdMedia');
  const res = await fn({ accountId, adsAccountId, adId, mediaType });

  return res.data;
};

export const refreshTikTokAdMedia = async (
  accountId: string,
  adsAccountId: string,
  adId: string,
  mediaType: 'image' | 'video',
): Promise<TikTokAdMediaRefreshResult> => {
  const fn = httpsCallable<
    {
      accountId: string;
      adsAccountId: string;
      adId: string;
      mediaType: 'image' | 'video';
    },
    TikTokAdMediaRefreshResult
  >(functions, 'accounts/adsAccounts/refreshTikTokAdMedia');
  const res = await fn({ accountId, adsAccountId, adId, mediaType });

  return res.data;
};
